/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useU2000GridStateHandlers } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU2000SearchStateHandlers } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import type { U2000GridStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000LUState } from '../u2000Core/reducer/formReducer/U2000LUState';
import type { U3151InitLU, U3151CustomerTypeSearchRow } from './U3151Service';

export type InitLU = U2000LUState['personalization'] &
  U3151InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3151ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3151CustomerTypeSearchRow>;
  initLU: U3151InitLU;
}

interface U3151ContextProviderProps {
  initLU: U3151InitLU;
  children: ReactNode;
}

const U3151Context = React.createContext<U3151ContextType | undefined>(undefined);

export const U3151ContextProvider: React.FC<U3151ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: 'u3151',
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3151CustomerTypeSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const contextValue = useMemo<U3151ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU],
  );

  return <U3151Context.Provider value={contextValue}>{children}</U3151Context.Provider>;
};

export function useU3151Context(): U3151ContextType {
  const context = useContext(U3151Context);
  if (!context) {
    throw new Error('useU3151Context must be used within a U3151ContextProvider');
  }
  return context;
}
